import React, { createContext, useContext, useMemo, useState } from "react";

export type ThemeMode = "light" | "dark" | "classic";

/**
 * Selectable themes, in the order they are offered in the theme picker.
 *
 * "light" and "dark" set the Fluent token set. "classic" shares light's tokens
 * and differs only in chrome: it keeps the navy header and left nav that light
 * mode carried before the white header landed.
 */
export const THEME_MODES: readonly ThemeMode[] = ["light", "dark", "classic"];

const isThemeMode = (value: string | null): value is ThemeMode =>
  value !== null && (THEME_MODES as readonly string[]).includes(value);

interface ThemeContextValue {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
}

export const THEME_STORAGE_KEY = "otl-theme-mode";

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getInitialThemeMode = (): ThemeMode => {
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (isThemeMode(stored)) {
    return stored;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getInitialThemeMode);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      themeMode,
      setThemeMode: (mode: ThemeMode): void => {
        window.localStorage.setItem(THEME_STORAGE_KEY, mode);
        setThemeModeState(mode);
      },
    }),
    [themeMode],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeModeProvider");
  }

  return context;
};
